/**
 * Modal: drop lifecycle story + full SIEM/audit activity (two requests).
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useMemo } from 'react';

import { useDropTimelineStoryAndFull } from '@/hooks';
import type { DropTimelineContext } from '@/features/siem/utils/drop-timeline';

import {
  formatDropTimelineModalLabel,
  formatDropTimelineWindow,
} from './drop-timeline-format';
import {
  DropTimelineModalCard,
  DropTimelineNestedDetails,
  effectiveDropTimelineContext,
  shouldShowDropTimelineModal,
  timelineModalHasQueryableId,
  useDropTimelineModalChrome,
} from './pick';

const STORY_SIEM_LIMIT = 50;
const FULL_SIEM_LIMIT = 120;

export function DropTimelineModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: DropTimelineContext | null;
}) {
  const chrome = useDropTimelineModalChrome(props.open);

  const effective = useMemo(
    () => effectiveDropTimelineContext(props.payload, chrome.picked),
    [props.payload, chrome.picked],
  );

  const pickList = props.payload?.pickTargets ?? [];
  const needsPick = pickList.length > 1 && chrome.picked == null;

  const uid = effective?.uniqueId?.trim() ?? '';
  const wid = effective?.wiserDropId;
  const prep = effective?.preparedDropId?.trim() ?? '';
  const queryEnabled =
    props.open &&
    !needsPick &&
    effective != null &&
    timelineModalHasQueryableId(effective);

  const { storyQ, fullQ } = useDropTimelineStoryAndFull({
    uniqueId: uid || undefined,
    wiserDropId: wid,
    preparedDropId: prep || undefined,
    enabled: queryEnabled,
    storyLimit: STORY_SIEM_LIMIT,
    fullLimit: FULL_SIEM_LIMIT,
  });

  const handleClose = () => props.onOpenChange(false);
  const showModal = props.open && shouldShowDropTimelineModal(props.payload);

  const windowStory = formatDropTimelineWindow(storyQ.data?.data.from, storyQ.data?.data.to);
  const windowFull = formatDropTimelineWindow(fullQ.data?.data.from, fullQ.data?.data.to);

  const subtitle = needsPick
    ? 'Choose a drop'
    : formatDropTimelineModalLabel({
        resolvedUniqueId: storyQ.data?.data.uniqueId ?? fullQ.data?.data.uniqueId,
        payloadUid: uid,
        preparedId: prep,
        wiserId: wid,
      });

  return (
    <>
      <AnimatePresence>
        {showModal ? (
          <motion.div
            className="fixed inset-0 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex items-center justify-center p-3 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Drop investigation timeline"
            >
              <DropTimelineModalCard
                subtitle={subtitle}
                needsPick={needsPick}
                pickList={pickList}
                picked={chrome.picked}
                setPicked={chrome.setPicked}
                onClose={handleClose}
                storyQ={storyQ}
                fullQ={fullQ}
                windowStory={windowStory}
                windowFull={windowFull}
                fullSiemLimit={FULL_SIEM_LIMIT}
                onSelectItem={chrome.openItem}
              />
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <DropTimelineNestedDetails
        siemId={chrome.siemId}
        auditId={chrome.auditId}
        onCloseSiem={chrome.closeSiem}
        onCloseAudit={chrome.closeAudit}
        onOpenAuditFromSiem={chrome.openAuditFromSiem}
      />
    </>
  );
}
